import { useMemo, useState } from "react";
import {
  Body1,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Label,
  Select,
} from "@fluentui/react-components";
import { AppButton } from "../common";
import dataTimeService from "../../services/dataTime.service";
import type { IResourceAllocationProjectApi } from "../../services/types/resourceAllocation";

interface AddResourceAllocationOrderDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedMonth: string;
  selectedYear: number;
  selectedProject: IResourceAllocationProjectApi | null;
  onSubmit: (payload: {
    assignmentDescription: string;
    durationMinutes: number;
    youTrackProjectId: string;
    youTrackProjectLabel: string;
  }) => Promise<void>;
  isSubmitting: boolean;
}

const AddResourceAllocationOrderDialog: React.FC<AddResourceAllocationOrderDialogProps> = ({
  open,
  setOpen,
  selectedMonth,
  selectedYear,
  selectedProject,
  onSubmit,
  isSubmitting,
}) => {
  const [assignmentDescription, setAssignmentDescription] = useState("");
  const [duration, setDuration] = useState("");
  const [validationError, setValidationError] = useState("");

  const resetForm = () => {
    setAssignmentDescription("");
    setDuration("");
    setValidationError("");
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const handleSubmit = async () => {
    if (!selectedProject) {
      setValidationError("Wybierz projekt na liście zleceń.");
      return;
    }

    if (!assignmentDescription.trim()) {
      setValidationError("Nazwa zlecenia jest wymagana.");
      return;
    }

    if (!dataTimeService.isValidTime(duration)) {
      setValidationError("Podaj czas w formacie HHH:MM.");
      return;
    }

    const durationMinutes = dataTimeService.convertStringToMin(duration);
    if (!durationMinutes || durationMinutes <= 0) {
      setValidationError("Ilość godzin musi być większa od zera.");
      return;
    }

    setValidationError("");
    await onSubmit({
      assignmentDescription: assignmentDescription.trim(),
      durationMinutes,
      youTrackProjectId: selectedProject.youTrackProjectId,
      youTrackProjectLabel: selectedProject.projectName,
    });
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Dodaj zlecenie</DialogTitle>
          <DialogContent style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <Label>Miesiąc</Label>
              <Input value={selectedMonth} readOnly />
            </div>
            <div>
              <Label>Rok</Label>
              <Input value={String(selectedYear)} readOnly />
            </div>
            <div>
              <Label>Nazwa projektu</Label>
              <Input value={selectedProject?.projectName ?? ""} readOnly />
            </div>
            <div>
              <Label required>Nazwa zlecenia</Label>
              <Input
                value={assignmentDescription}
                onChange={(_, data) => setAssignmentDescription(data.value)}
              />
            </div>
            <div>
              <Label required>Ilość godzin (HHH:MM)</Label>
              <Input
                value={duration}
                placeholder="np. 8:00"
                onChange={(_, data) => setDuration(data.value)}
              />
            </div>
            {validationError ? <span style={{ color: "red" }}>{validationError}</span> : null}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleClose}>
              Anuluj
            </Button>
            <AppButton
              text="Zapisz"
              onClick={handleSubmit}
              disabled={isSubmitting || !selectedProject}
            />
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default AddResourceAllocationOrderDialog;
